import { Box, Button, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAccountingRecordDetails } from 'src/shared/services/paymentService';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { AccountingRecordDetails } from '../models/accountingRecordDetails';

type AccountingRecordDetailModalProps = {
  modalOpen: boolean;
  verificationNo: number;
  ownerId: number;
  onClose: () => void;
};

// Listan över bokföringsunderlag visar bara antal och summa. Den här rutan visar vad ett
// underlag faktiskt består av, både inbetalningarna och det förbrukade tillgodot, så att det
// går att kontrollera ett verifikat utan att fråga databasen.
export const AccountingRecordDetailModal = (props: AccountingRecordDetailModalProps) => {
  const { modalOpen, verificationNo, ownerId, onClose } = props;
  const { t } = useTranslation(['common', 'invoice']);
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);
  const [details, setDetails] = useState<AccountingRecordDetails>({ payments: [], credits: [], vouchers: [] });

  const fetchDetails = useCallback(async () => {
    if (!verificationNo || !ownerId) return;

    setLoading(true);

    try {
      const res = await getAccountingRecordDetails(verificationNo, ownerId);
      if (isMounted()) setDetails(res.data);
    } catch (error) {
      if (isMounted()) ConsoleHelper.log(error);
    } finally {
      if (isMounted()) setLoading(false);
    }
  }, [isMounted, ownerId, verificationNo]);

  useEffect(() => {
    if (modalOpen) fetchDetails();
  }, [fetchDetails, modalOpen]);

  const onHandleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <GenericDialog
      open={modalOpen}
      maxWidth='lg'
      dialogTitle={t('invoice:accountingRecordDetails.title', { number: verificationNo })}
      onClose={onHandleClose}
      onSave={onHandleClose}
      dialogContent={
        <Box sx={{ minHeight: '10rem' }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {!loading && (
            <>
              {/* Verifikatet först, eftersom det är det som faktiskt bokförs. Raderna nedanför
                  visar vad det består av. */}
              {details.vouchers.length > 0 && (
                <>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    {t('invoice:accountingRecordDetails.voucher')}
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('invoice:accountingRecordDetails.voucherDate')}</TableCell>
                          <TableCell>{t('invoice:accountingRecordDetails.voucherAccount')}</TableCell>
                          <TableCell>{t('invoice:accountingRecordDetails.voucherText')}</TableCell>
                          <TableCell align='right'>{t('invoice:accountingRecordDetails.voucherDebit')}</TableCell>
                          <TableCell align='right'>{t('invoice:accountingRecordDetails.voucherCredit')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {details.vouchers.map((row) => (
                          <TableRow key={`${row.date}-${row.account}-${row.text}-${row.debit}-${row.credit}`}>
                            <TableCell>{row.date}</TableCell>
                            <TableCell>{row.account}</TableCell>
                            <TableCell>{row.text}</TableCell>
                            <TableCell align='right'>{row.debit > 0 ? <AmountWithCurrency amount={row.debit} /> : ''}</TableCell>
                            <TableCell align='right'>{row.credit > 0 ? <AmountWithCurrency amount={row.credit} /> : ''}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}

              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                {t('invoice:accountingRecordDetails.payments', { count: details.payments.length })}
              </Typography>

              {details.payments.length === 0 && (
                <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                  {t('invoice:accountingRecordDetails.noPayments')}
                </Typography>
              )}

              {details.payments.length > 0 && (
                <Box sx={{ maxHeight: '18rem', overflow: 'auto', mb: 3 }}>
                  <Table size='small' stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('invoice:date')}</TableCell>
                        <TableCell>{t('invoice:paymentHeader')}</TableCell>
                        <TableCell>{t('invoice:invoiceMission')}</TableCell>
                        <TableCell>{t('invoice:OCR')}</TableCell>
                        <TableCell align='right'>{t('invoice:amount')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {details.payments.map((item) => (
                        <TableRow key={item.invoicePayment_id}>
                          <TableCell>{item.date}</TableCell>
                          <TableCell>{item.source}</TableCell>
                          <TableCell>{item.invoiceMissionName}</TableCell>
                          <TableCell>{item.ocr}</TableCell>
                          <TableCell align='right'>
                            <AmountWithCurrency amount={item.amount} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {/* Tillgodo visas bara när underlaget innehåller något, så vanliga underlag ser ut
                  precis som förut. */}
              {details.credits.length > 0 && (
                <>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    {t('invoice:accountingRecordDetails.credits', { count: details.credits.length })}
                  </Typography>

                  <Box sx={{ maxHeight: '18rem', overflow: 'auto' }}>
                    <Table size='small' stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('invoice:transactionPage.unbookedCreditsDate')}</TableCell>
                          <TableCell>{t('invoice:transactionPage.unbookedCreditsMember')}</TableCell>
                          <TableCell>{t('invoice:transactionPage.unbookedCreditsInvoice')}</TableCell>
                          <TableCell align='right'>{t('invoice:transactionPage.unbookedCreditsAmount')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {details.credits.map((item) => (
                          <TableRow key={item.invoiceCreditId}>
                            <TableCell>{dayjs(item.created).format('YYYY-MM-DD')}</TableCell>
                            <TableCell>{`${item.memberNr ?? ''} ${item.firstName ?? ''} ${item.lastName ?? ''}`.trim()}</TableCell>
                            <TableCell>{item.ocr}</TableCell>
                            <TableCell align='right'>
                              <AmountWithCurrency amount={item.amount} />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      }
      dialogActions={
        <Button variant='outlined' disableElevation onClick={onHandleClose}>
          {t('common:button.close')}
        </Button>
      }
    />
  );
};
